import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"
import { createSelect2Manager } from "../lib/select2_manager"
import { showAlertModal, showConfirmModal } from "../lib/custom_confirm"
import { formatDeliveryDaysSummary } from "../lib/delivery_days_summary"
import {
  confirmStatesIfUnlicensed,
  renderUnlicensedStatesNotice,
  SELF_SUBJECT,
} from "../lib/unlicensed_states_notice"

const isFunction = (fn) => typeof fn === "function"
// Must match Admin::LeadOrdersController::MINIMUM_FREE_LEADS_REASON_LENGTH
const MINIMUM_FREE_LEADS_REASON_LENGTH = 10

// Drives the lead order create/edit FORM (lead_orders/new + realtime_lead_orders/new): GHL tags, photo upload, webhook validation, notes. (Renamed from lead-order-edit — there is no separate edit page.)
// Connects to data-controller="lead-order-form"
export default class extends Controller {
  static targets = [
    "select2",
    "freeLeadCountInput",
    "addFreeLeadsButton",
    "ringyIntegrationSelect",
    "ringyMap",
    "ringySidDisplay",
    "ringyAuthTokenDisplay",
    "distributionButton",
    "ghlTagsSelect",
    "ghlTagsContainer",
    "ghlTagsField",
    "disabledToggle",
    "googleSheetExportButton",
    "googleSheetExportTimeAgo",
    "webhookUrlInput",
    "validateWebhookButton",
    "webhookValidationStatus",
    "agedBucketSelect",
    "daysSummary",
    "unlicensedStatesNotice",
    // Free leads modal targets
    "freeLeadsReasonInput",
    "freeLeadsError"
  ]

  static values = {
    addFreeLeadsUrl: String,
    pageReloadTimeout: Number,
    leadOrderId: Number,
    statesReadonly: Boolean,
    googleSheetExportTimeout: { type: Number, default: 900000 }, // 15 minutes in ms
    lastGoogleSheetExportAt: String, // ISO8601 timestamp from server
    userIanaTimezone: { type: String, default: 'America/New_York' },
    priority: { type: Number, default: 0 }
  }

  initialize() {
    this.isDistributing = false
  }

  connect() {
    this.select2Manager = createSelect2Manager()
    this.initializeSelect2s()
    this.setDefaultNotificationValues()
    this.setupFormValidation()
    this.setupFormSubmitHandler()
    this.checkGoogleSheetExportCooldown()
    this.setupTabHandlers()
    this.setupUnlicensedStatesNotice()
    this.priorityResetConfirmedFlag = false
  }

  disconnect() {
    this.teardownUnlicensedStatesNotice()
    this.select2Manager?.destroyAll()
    this.removeTabHandlers()
    // Abort any pending priority-guard confirm listener so it can't fire on a
    // disconnected controller (e.g., Turbo navigation while the modal is open).
    if (this._priorityGuardAbort) this._priorityGuardAbort.abort()
    // Clear Google Sheet export cooldown interval to prevent memory leaks
    if (this.googleSheetCooldownInterval) {
      clearInterval(this.googleSheetCooldownInterval)
      this.googleSheetCooldownInterval = null
    }
  }

  // -- Tab Handling -----------------------------------------------------------
  // Bootstrap tabs with Select2 require reinitialization when shown

  setupTabHandlers() {
    // Store the bound handler so we can remove it later
    this.tabShownHandler = (event) => this.handleTabShown(event)

    // Listen for Bootstrap tab shown events
    const tabButtons = this.element.querySelectorAll('[data-bs-toggle="pill"]')
    tabButtons.forEach((button) => {
      button.addEventListener("shown.bs.tab", this.tabShownHandler)
    })
  }

  removeTabHandlers() {
    const tabButtons = this.element.querySelectorAll('[data-bs-toggle="pill"]')
    tabButtons.forEach((button) => {
      button.removeEventListener("shown.bs.tab", this.tabShownHandler)
    })
  }

  handleTabShown(event) {
    // When a tab is shown, reinitialize any Select2 dropdowns in that tab
    // This is necessary because Select2 doesn't render correctly in hidden elements
    const targetId = event.target.getAttribute("data-bs-target")
    if (!targetId) return

    const tabPane = this.element.querySelector(targetId)
    if (!tabPane) return

    // Find and reinitialize Select2 elements in the newly shown tab
    const select2Elements = tabPane.querySelectorAll("[data-lead-order-form-target='select2']")
    select2Elements.forEach((element) => {
      // Destroy and reinitialize to ensure proper rendering
      if (window.$ && window.$(element).data("select2")) {
        // Force a resize/reposition of the Select2 dropdown
        window.$(element).select2("close")
      }
    })
  }

  setDefaultNotificationValues() {
    const sendEmailCheckbox = this.element.querySelector("#lead_order_send_email")
    const sendTextCheckbox = this.element.querySelector("#lead_order_send_text")

    // Only set defaults if the checkboxes exist and are not already checked
    if (sendEmailCheckbox && !sendEmailCheckbox.checked) {
      sendEmailCheckbox.checked = true
    }

    if (sendTextCheckbox && !sendTextCheckbox.checked) {
      sendTextCheckbox.checked = true
    }
  }

  // -- Select2 --------------------------------------------------------------

  initializeSelect2s() {
    this.select2Targets.forEach((element) => {
      this.select2Manager?.init(element, () => this.buildSelect2Options(element))
    })
  }

  buildSelect2Options(element) {
    const { dataset } = element
    const options = {}

    if (dataset.select2Placeholder) options.placeholder = dataset.select2Placeholder
    if (dataset.select2AllowClear !== undefined) {
      options.allowClear = dataset.select2AllowClear === "true"
    }
    if (dataset.select2Multiple !== undefined) {
      options.multiple = dataset.select2Multiple === "true"
    }
    if (dataset.select2Width) options.width = dataset.select2Width
    if (dataset.select2MinimumResultsForSearch) {
      options.minimumResultsForSearch = Number(dataset.select2MinimumResultsForSearch)
    }
    if (dataset.select2MinimumInputLength) {
      options.minimumInputLength = Number(dataset.select2MinimumInputLength)
    }
    if (dataset.select2DropdownParent) {
      options.dropdownParent = document.querySelector(dataset.select2DropdownParent)
    }

    if (dataset.select2AjaxUrl) {
      const delay = Number(dataset.select2AjaxDelay || 250)
      options.ajax = {
        url: dataset.select2AjaxUrl,
        dataType: "json",
        delay,
        data: (params) => ({ q: params.term }),
        processResults: (data) => ({ results: data }),
        cache: true
      }
    }

    if (this.statesReadonlyValue && element.name?.includes("lead_order[states]")) {
      options.disabled = true
    }

    return options
  }

  get jQuery() {
    return window.jQuery || window.$
  }

  get statesSelect() {
    return this.select2Targets.find((target) => target.name === "lead_order[states][]")
  }

  get leadQualitiesSelect() {
    return this.select2Targets.find((target) => target.classList.contains("select2-lead-quality"))
  }

  // -- Free leads ----------------------------------------------------------

  submitFreeLeadsFromModal(event) {
    event.preventDefault()

    const url = this.addFreeLeadsUrlValue
    if (!url) {
      this.showFreeLeadsError("Unable to determine destination for free lead request.")
      return
    }

    const leadCount = this.currentLeadCount()
    if (!Number.isInteger(leadCount) || leadCount === 0) {
      this.showFreeLeadsError("Please enter a non-zero number of leads to adjust.")
      return
    }

    const reason = this.currentFreeLeadsReason()
    if (!reason || reason.trim().length < MINIMUM_FREE_LEADS_REASON_LENGTH) {
      this.showFreeLeadsError(`Please provide a detailed reason (minimum ${MINIMUM_FREE_LEADS_REASON_LENGTH} characters).`)
      return
    }

    this.hideFreeLeadsError()
    this.submitFreeLeads(url, leadCount, reason)
  }

  async submitFreeLeads(url, leadCount, reason = "") {
    if (typeof window.fetch !== "function") {
      this.showFreeLeadsError("Unable to submit free lead request.")
      return
    }

    const params = { lead_count: leadCount }
    if (reason) {
      params.reason = reason
    }
    const body = new URLSearchParams(params).toString()

    try {
      const response = await window.fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "X-CSRF-Token": this.csrfToken
        },
        body
      })

      const data = await this.safeJson(response)

      if (response.ok) {
        const message = data?.message || "Free leads added."
        this.notifySuccess(message)
        this.resetFreeLeadsModal()
        this.closeFreeLeadsModal()
        this.scheduleReload()
        return
      }

      const message = data?.error || "Unable to add free leads. Please try again."
      this.showFreeLeadsError(message)
    } catch (error) {
      console.error("Failed to add free leads.", error)
      this.showFreeLeadsError("Error adding free leads")
    }
  }

  showFreeLeadsError(message) {
    if (this.hasFreeLeadsErrorTarget) {
      this.freeLeadsErrorTarget.textContent = message
      this.freeLeadsErrorTarget.classList.remove("d-none")
    } else {
      this.notifyError(message)
    }
  }

  hideFreeLeadsError() {
    if (this.hasFreeLeadsErrorTarget) {
      this.freeLeadsErrorTarget.classList.add("d-none")
    }
  }

  currentFreeLeadsReason() {
    if (!this.hasFreeLeadsReasonInputTarget) return null
    return this.freeLeadsReasonInputTarget.value
  }

  resetFreeLeadsModal() {
    this.resetLeadCount()
    if (this.hasFreeLeadsReasonInputTarget) {
      this.freeLeadsReasonInputTarget.value = ""
    }
    this.hideFreeLeadsError()
  }

  closeFreeLeadsModal() {
    const modal = document.getElementById("addFreeLeadsModal")
    if (modal && typeof bootstrap !== "undefined") {
      const bsModal = bootstrap.Modal.getInstance(modal)
      if (bsModal) {
        bsModal.hide()
      }
    }
  }

  preventInvalidKey(event) {
    const allowedKeys = [
      "Backspace",
      "Tab",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End"
    ]

    if (event.ctrlKey || event.metaKey || allowedKeys.includes(event.key)) {
      return
    }

    if (["+", "e", "E"].includes(event.key)) {
      event.preventDefault()
    }
  }

  sanitizeInput(event) {
    const target = event?.currentTarget || this.freeLeadCountInputTarget
    if (!target) return

    const value = parseInt(target.value, 10)
    if (Number.isNaN(value)) {
      target.value = ""
    }
  }

  currentLeadCount() {
    if (!this.hasFreeLeadCountInputTarget) return null
    return parseInt(this.freeLeadCountInputTarget.value, 10)
  }

  resetLeadCount() {
    if (this.hasFreeLeadCountInputTarget) {
      this.freeLeadCountInputTarget.value = ""
    }
  }

  async distributeLeads(event) {
    event.preventDefault()

    if (this.isDistributing) return

    const button = event.currentTarget
    const service = button?.dataset?.service
    if (!service) {
      this.notifyError("Unable to determine which service to use.")
      return
    }

    const url = this.distributionUrl
    if (!url) {
      this.notifyError("Unable to determine distribution endpoint.")
      return
    }

    if (typeof window.fetch !== "function") {
      this.notifyError("Lead distribution is not supported in this browser.")
      return
    }

    this.isDistributing = true
    this.setButtonLoadingState(button, true)
    this.toggleDistributionButtons(true)

    try {
      const response = await window.fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ service })
      })

      const data = await this.safeJson(response)
      if (response.ok) {
        const message = data?.message || `Queued leads for ${this.serviceLabel(service)}.`
        this.notifySuccess(message)
      } else {
        const message =
          data?.error || `Failed to send leads to ${this.serviceLabel(service)}.`
        this.notifyError(message)
      }
    } catch (error) {
      console.error("Failed to distribute leads.", error)
      this.notifyError(`Failed to send leads to ${this.serviceLabel(service)}.`)
    } finally {
      this.setButtonLoadingState(button, false)
      this.toggleDistributionButtons(false)
      this.isDistributing = false
    }
  }

  get distributionUrl() {
    const id = this.leadOrderIdValue
    if (!id) return null
    return `/lead_orders/${id}/distribute_leads`
  }

  toggleDistributionButtons(disabled) {
    if (!this.hasDistributionButtonTarget) return
    this.distributionButtonTargets.forEach((btn) => {
      if (disabled) {
        btn.setAttribute("disabled", "disabled")
      } else {
        btn.removeAttribute("disabled")
      }
    })
  }

  setButtonLoadingState(button, loading) {
    if (!button) return

    if (loading) {
      if (!button.dataset.originalContent) {
        button.dataset.originalContent = button.innerHTML
      }
      button.innerHTML = `<span class="gl-btn-spinner" role="status" aria-hidden="true"></span>${button.dataset.originalContent}`
      button.setAttribute("aria-busy", "true")
      return
    }

    if (button.dataset.originalContent) {
      button.innerHTML = button.dataset.originalContent
    }
    button.removeAttribute("aria-busy")
  }

  serviceLabel(service) {
    const normalized = (service || "").toString().toLowerCase()
    switch (normalized) {
      case "webhook":
        return "Webhook"
      case "ringy":
        return "Ringy"
      case "ghl":
        return "GHL"
      default:
        return normalized.charAt(0).toUpperCase() + normalized.slice(1)
    }
  }

  resolveAddFreeLeadsUrl(button) {
    return (
      this.addFreeLeadsUrlValue ||
      button?.getAttribute("href") ||
      button?.dataset?.url ||
      ""
    )
  }

  // -- States & days -------------------------------------------------------

  selectAllStates(event) {
    event.preventDefault()

    const select = this.statesSelect
    if (!select) return

    const values = Array.from(select.options)
      .map((option) => option.value)
      .filter((value) => value && value !== "NY")

    this.setSelectValues(select, values)
  }

  copyStatesFromProfile(event) {
    event.preventDefault()

    const select = this.statesSelect
    if (!select) return

    const raw = event.currentTarget.dataset.licensedStates
    const states = this.parseJson(raw) || []

    if (!states.length) {
      showAlertModal(
        "No licensed states to copy from your profile. New York is never copied — select it by hand if you want it.",
        { variant: "warning", title: "No licensed states" }
      )
      return
    }

    this.setSelectValues(select, states)
  }

  // -- Unlicensed-state advisory -------------------------------------------

  // Wire the notice once and paint the initial state, so an order that ALREADY
  // carries unlicensed states (an edit, or a prefill copied from another order)
  // is flagged on load rather than only after the buyer touches the picker.
  setupUnlicensedStatesNotice() {
    if (!this.hasUnlicensedStatesNoticeTarget) return

    const select = this.statesSelect
    if (!select) return

    // Select2 fires its selection changes as a jQuery event, which native
    // addEventListener handlers never see; the native fallback control does the
    // reverse. Bind through jQuery when present (it ships as Select2's own
    // dependency and fires for the native control too) and fall back otherwise.
    const jQuery = this.jQuery
    const refresh = () => this.refreshUnlicensedStatesNotice()

    if (jQuery) {
      this._unlicensedStatesRefresh = refresh
      jQuery(select).on("change.unlicensedStates", refresh)
    } else {
      this._unlicensedStatesTeardown = () => select.removeEventListener("change", refresh)
      select.addEventListener("change", refresh)
    }

    refresh()
  }

  teardownUnlicensedStatesNotice() {
    const select = this.statesSelect
    const jQuery = this.jQuery

    if (jQuery && select && this._unlicensedStatesRefresh) {
      jQuery(select).off("change.unlicensedStates", this._unlicensedStatesRefresh)
      this._unlicensedStatesRefresh = null
    }

    this._unlicensedStatesTeardown?.()
    this._unlicensedStatesTeardown = null
  }

  // Licensed states belong to the ORDER's user, not the viewer — an admin edit
  // or an owner-funded build renders this form for someone else's book.
  get unlicensedStatesContext() {
    const notice = this.hasUnlicensedStatesNoticeTarget ? this.unlicensedStatesNoticeTarget : null

    return {
      licensed: this.parseJson(notice?.dataset.licensedStates) || [],
      subject: notice?.dataset.subject || SELF_SUBJECT,
      profileUrl: notice?.dataset.profileUrl || null,
    }
  }

  selectedStates() {
    const select = this.statesSelect
    if (!select) return []

    return Array.from(select.selectedOptions || [])
      .map((option) => option.value)
      .filter(Boolean)
  }

  refreshUnlicensedStatesNotice() {
    if (!this.hasUnlicensedStatesNoticeTarget) return []

    return renderUnlicensedStatesNotice(this.unlicensedStatesNoticeTarget, {
      selected: this.selectedStates(),
      ...this.unlicensedStatesContext,
    })
  }

  selectAllDays(event) {
    event.preventDefault()
    this.toggleDayCheckboxes(true)
  }

  clearAllDays(event) {
    event.preventDefault()
    showAlertModal(
      "You must have at least one day per week selected. Please select the days you want before clearing.",
      { variant: "warning", title: "Selection required" }
    )
  }

  copyDaysFromProfile(event) {
    event.preventDefault()
    const raw = event.currentTarget.dataset.defaultDays
    const days = this.parseJson(raw) || []

    this.dayCheckboxes().forEach((checkbox) => {
      checkbox.checked = days.includes(checkbox.value)
    })
    this.updateDaysSummary()
  }

  toggleDayCheckboxes(value) {
    this.dayCheckboxes().forEach((checkbox) => {
      checkbox.checked = value
    })
    this.updateDaysSummary()
  }

  dayCheckboxes() {
    return Array.from(
      this.element.querySelectorAll('input[name="lead_order[days_per_week][]"]')
    )
  }

  updateDaysSummary() {
    if (!this.hasDaysSummaryTarget) return
    const days = this.getCheckedDays().map((checkbox) => checkbox.value)
    this.daysSummaryTarget.textContent = `(${formatDeliveryDaysSummary(days)})`
  }

  setSelectValues(select, values) {
    const jQuery = this.jQuery
    if (jQuery) {
      jQuery(select).val(values).trigger("change")
      return
    }

    Array.from(select.options).forEach((option) => {
      option.selected = values.includes(option.value)
    })

    // The jQuery branch's trigger("change") drives the unlicensed-state notice;
    // the native branch mutates `selected` directly and fires nothing, so both
    // callers (Select All / Copy From Profile) would otherwise leave a stale
    // notice on a page where Select2 never initialized.
    this.refreshUnlicensedStatesNotice()
  }

  // -- Webhook integration -------------------------------------------------

  copyWebhookFromProfile(event) {
    event.preventDefault()

    const webhookUrl = event.currentTarget.dataset.webhookUrl

    if (!webhookUrl) {
      showAlertModal(
        "No webhook URL found in your profile. Please update your profile with a webhook URL.",
        { variant: "warning", title: "No webhook URL" }
      )
      return
    }

    if (this.hasWebhookUrlInputTarget) {
      this.webhookUrlInputTarget.value = webhookUrl
      this.notifySuccess("Webhook URL copied from profile.")
    }
  }

  async validateWebhook(event) {
    event.preventDefault()

    const id = this.leadOrderIdValue
    if (!id) {
      this.notifyError("Unable to determine lead order.")
      return
    }

    const button = event.currentTarget
    this.setButtonLoadingState(button, true)
    button.setAttribute("disabled", "disabled")

    try {
      const response = await window.fetch(`/lead_orders/${id}/validate_webhook`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })

      const data = await this.safeJson(response)

      if (!response.ok) {
        this.notifyError(data?.error || data?.message || `Server error (${response.status})`)
        return
      }

      if (this.hasWebhookValidationStatusTarget) {
        if (data?.valid) {
          this.webhookValidationStatusTarget.innerHTML =
            '<span class="text-success"><i class="ph-fill ph-check-circle me-1"></i>Valid</span>'
          this.notifySuccess(data.message || "Webhook URL is valid.")
        } else {
          this.webhookValidationStatusTarget.innerHTML =
            '<span class="text-danger"><i class="ph-fill ph-x-circle me-1"></i>Invalid</span>'
          this.notifyError(data?.message || data?.error || "Webhook validation failed.")
        }
      }
    } catch (error) {
      console.error("Webhook validation failed.", error)
      this.notifyError("Webhook validation failed.")
    } finally {
      this.setButtonLoadingState(button, false)
      button.removeAttribute("disabled")
    }
  }

  clearWebhookValidationStatus() {
    if (this.hasWebhookValidationStatusTarget) {
      this.webhookValidationStatusTarget.innerHTML = ""
    }
  }

  // -- Ringy integration ---------------------------------------------------

  updateRingyIntegration() {
    if (!this.hasRingyMapTarget) return

    const selectedId = this.ringyIntegrationSelectTarget?.value
    const mapping = this.parseJson(this.ringyMapTarget.value) || {}
    const data = selectedId ? mapping[selectedId] : null

    if (data) {
      if (this.hasRingySidDisplayTarget) {
        this.ringySidDisplayTarget.value = data.sid || ""
      }
      if (this.hasRingyAuthTokenDisplayTarget) {
        this.ringyAuthTokenDisplayTarget.value = data.auth_token || ""
      }
    } else {
      if (this.hasRingySidDisplayTarget) {
        this.ringySidDisplayTarget.value = ""
      }
      if (this.hasRingyAuthTokenDisplayTarget) {
        this.ringyAuthTokenDisplayTarget.value = ""
      }
    }
  }

  // -- Agent field helpers -------------------------------------------------

  clearAgentFields(event) {
    event.preventDefault()

    this.setInputValue("lead_order[display_agent_name]", "")
    this.setInputValue("lead_order[display_agent_title]", "")
    this.setInputValue("lead_order[display_npn]", "")
    this.setInputValue("lead_order[agent_email]", "")
    this.setInputValue("lead_order[agent_phone]", "")
    this.setInputValue("lead_order[display_calendar_link]", "")
    this.setInputValue("lead_order[display_agent_website]", "")

    this.setCheckboxValue("lead_order[send_lead_text]", false)
    this.setCheckboxValue("lead_order[send_lead_email]", false)
    this.setCheckboxValue("lead_order[send_text]", false)
    this.setCheckboxValue("lead_order[send_email]", false)

    this.setRemovePhotoFlag(true)

    const preview = this.element.querySelector("#lead-order-photo-preview")
    if (preview) preview.remove()

    const fileInput = this.element.querySelector('input[name="lead_order[photo]"]')
    if (fileInput) fileInput.value = ""

    const copyFlag = this.element.querySelector("#copy-photo-flag")
    if (copyFlag) copyFlag.value = "0"
  }

  copyFromProfile(event) {
    event.preventDefault()
    const dataset = event.currentTarget.dataset || {}

    const photoUrl = dataset.photoUrl
    const copyFlag = this.element.querySelector("#copy-photo-flag")

    if (photoUrl && copyFlag) {
      copyFlag.value = "1"
      const wrapper = this.element.querySelector("#photo-wrapper")
      if (wrapper) {
        let preview = this.element.querySelector("#lead-order-photo-preview")
        if (!preview) {
          preview = document.createElement("div")
          preview.id = "lead-order-photo-preview"
          preview.classList.add("mb-2")
          wrapper.prepend(preview)
        }
        // Use DOM API to prevent XSS via photo URL
        preview.innerHTML = ""
        const img = document.createElement("img")
        img.src = photoUrl
        img.className = "img-thumbnail"
        img.alt = "Agent Photo Preview"
        preview.appendChild(img)
      }

      this.setRemovePhotoFlag(false)
    } else if (copyFlag) {
      copyFlag.value = "0"
      this.setRemovePhotoFlag(true)
      this.element.querySelector("#lead-order-photo-preview")?.remove()
      const fileInput = this.element.querySelector('input[name="lead_order[photo]"]')
      if (fileInput) fileInput.value = ""
    }

    this.copyContactFieldsFromDataset(dataset)
  }

  // Copies ONLY the contact/display fields + notification flags from a data
  // attribute set — no photo side effects; copyFromProfile layers the photo
  // handling above on top.
  copyContactFieldsFromDataset(dataset) {
    this.setInputValue("lead_order[display_agent_name]", dataset.name || "")
    this.setInputValue(
      "lead_order[display_agent_title]",
      dataset.agentTitle || dataset["agent-title"] || ""
    )
    this.setInputValue("lead_order[display_npn]", dataset.npn || "")
    this.setInputValue("lead_order[agent_email]", dataset.email || "")
    this.setInputValue("lead_order[agent_phone]", dataset.phone || "")
    this.setInputValue(
      "lead_order[display_calendar_link]",
      dataset.calendarLink || dataset["calendar-link"] || ""
    )
    this.setInputValue(
      "lead_order[display_agent_website]",
      dataset.agentWebsite || dataset["agent-website"] || ""
    )

    this.setCheckboxValue("lead_order[send_lead_text]", this.datasetToBool(dataset.sendLeadSms))
    this.setCheckboxValue("lead_order[send_lead_email]", this.datasetToBool(dataset.sendLeadEmail))
    this.setCheckboxValue("lead_order[send_text]", this.datasetToBool(dataset.sendSms))
    this.setCheckboxValue("lead_order[send_email]", this.datasetToBool(dataset.sendEmail))
  }

  setInputValue(name, value) {
    const input = this.element.querySelector(`input[name="${name}"]`)
    if (input) {
      input.value = value
      // Assigning .value fires nothing, so listeners on this input never see
      // it. copyFromProfile and clearAgentFields both populate/blank
      // agent_phone and agent_email through here, and notification-gate keys
      // its SMS/Email checkboxes off exactly those fields — without a dispatch
      // Copy From Profile leaves the boxes disabled next to a visibly-filled
      // destination, and Clear leaves them enabled next to an empty one.
      input.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  setCheckboxValue(name, value) {
    const checkbox = this.element.querySelector(`input[type="checkbox"][name="${name}"]`)
    if (checkbox) {
      checkbox.checked = Boolean(value)
      // bubbles: true — the un-bubbled version only reached handlers bound
      // directly to this element, so a controller scoped to an ancestor (the
      // form) never saw it.
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  setRemovePhotoFlag(value) {
    const field = this.element.querySelector('input[name="lead_order[remove_photo]"]')
    if (!field) return

    const normalized = value ? "1" : "0"

    if (field.type === "checkbox") {
      field.checked = Boolean(value)
      field.value = normalized
    } else {
      field.value = normalized
    }

    field.dispatchEvent(new Event("change"))
  }

  datasetToBool(value) {
    return value === true || value === "true" || value === "1" || value === 1
  }

  setupFormValidation() {
    const dayCheckboxes = this.element.querySelectorAll('input[name="lead_order[days_per_week][]"]')
    dayCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        const checkedDays = this.getCheckedDays()
        if (checkedDays.length === 0) {
          checkbox.checked = true
          showAlertModal("You must have at least one day per week selected.", { variant: "warning", title: "Selection required" })
        }
        this.updateDaysSummary()
      })
    })
  }

  setupFormSubmitHandler() {
    const form = this.element.closest('form') || this.element.querySelector('form');
    if (!form) return;

    if (form.dataset.leadOrderEditHandlerAttached) return;
    form.dataset.leadOrderEditHandlerAttached = "true";

    form.addEventListener('submit', (event) => {
      this.enableDaysPerWeekCheckboxes();
      this.gateSubmitOnUnlicensedStates(event, form);
    });
  }

  // Last chance to catch a state picked by accident. The modal is async and
  // submit is not, so always preventDefault, then requestSubmit() once
  // acknowledged — `unlicensedStatesConfirmed` keeps the re-entrant submit from
  // re-prompting. (Pattern per CLAUDE.md / bucket_maint_controller.)
  gateSubmitOnUnlicensedStates(event, form) {
    if (this.unlicensedStatesConfirmed) {
      this.unlicensedStatesConfirmed = false
      return
    }

    const offenders = this.refreshUnlicensedStatesNotice()
    if (!offenders.length) return

    event.preventDefault()

    // Preserve which button was pressed. These forms carry TWO submits whose
    // params[:commit] pick different server paths ('Save and Checkout' vs
    // 'Add to Cart'), and a bare requestSubmit() re-submits through the FIRST
    // one — silently changing the action the buyer asked for.
    const submitter = event.submitter || null

    confirmStatesIfUnlicensed(this.selectedStates(), {
      ...this.unlicensedStatesContext,
      actionLabel: this.submitActionLabel(submitter),
    }).then((accepted) => {
      // Bail if declined, or if the form was detached while the modal was open.
      if (!accepted || !form.isConnected) return

      this.unlicensedStatesConfirmed = true
      if (submitter && submitter.isConnected) {
        form.requestSubmit(submitter)
      } else {
        form.requestSubmit()
      }
    })
  }

  // The confirm asks "<label> anyway?", so the label has to name the button the
  // buyer actually pressed. These forms carry more than one submit ("Save and
  // Checkout" vs "Add to Cart"), and a hardcoded label would describe the wrong
  // action on whichever one it did not name.
  // Echoes the button's own label verbatim ("Add to Cart anyway?") so the buyer
  // matches the prompt to what they clicked without translating.
  submitActionLabel(submitter) {
    return (submitter?.value || "").trim() || "save this order"
  }

  enableDaysPerWeekCheckboxes() {
    const dayCheckboxes = this.element.querySelectorAll('input[name="lead_order[days_per_week][]"]');
    dayCheckboxes.forEach((checkbox) => {
      checkbox.disabled = false;
    });
  }

  validateDaysPerWeek() {
    const checkedDays = this.getCheckedDays()
    return checkedDays.length > 0
  }

  getCheckedDays() {
    const checkboxes = this.element.querySelectorAll('input[name="lead_order[days_per_week][]"]:checked')
    return Array.from(checkboxes)
  }

  scheduleReload() {
    if (
      typeof window.location?.reload !== "function" ||
      this.isJsdomEnvironment()
    ) {
      return
    }

    const timeout = this.pageReloadTimeoutValue || 1500
    window.setTimeout(() => {
      try {
        window.location.reload()
      } catch (error) {
        console.warn("Unable to reload page.", error)
      }
    }, timeout)
  }

  get csrfToken() {
    return (
      document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
    )
  }

  safeJson(response) {
    if (!response || !response.clone) return Promise.resolve(null)
    try {
      return response.clone().json()
    } catch (_error) {
      return Promise.resolve(null)
    }
  }

  notifySuccess(message) {
    if (isFunction(window.showToast)) {
      window.showToast(message)
    } else {
      console.info(message)
    }
  }

  notifyError(message) {
    if (isFunction(window.showToast)) {
      window.showToast(message, "bg-danger")
    } else {
      console.error(message)
    }
  }

  notifyWarning(message) {
    if (isFunction(window.showToast)) {
      window.showToast(message, "bg-warning")
    } else {
      console.warn(message)
    }
  }

  parseJson(value) {
    if (!value) return null
    try {
      return JSON.parse(value)
    } catch (_error) {
      return null
    }
  }

  formatTimestamp(value) {
    if (!value) return ""
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    })
  }

  isJsdomEnvironment() {
    const ua = window.navigator?.userAgent || ""
    return ua.toLowerCase().includes("jsdom")
  }

  // GHL Tags Management
  updateGhlTags() {
    if (!this.hasGhlTagsSelectTarget || !this.hasGhlTagsContainerTarget) {
      return
    }

    const selectedOptions = Array.from(this.ghlTagsSelectTarget.selectedOptions)
    const selectedTags = selectedOptions.map(option => option.value)

    // Update hidden fields
    const container = this.ghlTagsContainerTarget
    container.innerHTML = ''

    selectedTags.forEach((tag) => {
      const hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = 'lead_order[ghl_tags][]'
      hiddenField.value = tag
      hiddenField.setAttribute('data-lead-order-form-target', 'ghlTagsField')
      container.appendChild(hiddenField)
    })
  }

  selectAllAvailableTags(event) {
    if (event) {
      event.preventDefault()
    }

    if (!this.hasGhlTagsSelectTarget) {
      return
    }

    // Select all options in the select
    const allOptions = Array.from(this.ghlTagsSelectTarget.options)
    allOptions.forEach(option => {
      option.selected = true
    })

    // Sync hidden-field mirror if the page uses one (legacy _form_fields.haml).
    // The new edit-tab partial submits via native select[name] so this is a no-op there.
    if (this.hasGhlTagsContainerTarget) {
      this.updateGhlTags()
    }
  }

  // Lead-flow pause lives ONLY in the React panel (LeadOrderPanelBody →
  // PATCH toggle_active, capped by LeadOrder#pause_limit_error). The buy form
  // has no Order Status toggle and no client-side pause limit (owner ruling
  // 2026-09-18).
  guardPauseWithPriority(proceedFn) {
    // Short-circuit when no warning is needed: priority is zero/negative, OR
    // the user already confirmed in this session (network-error retry path).
    if ((this.priorityValue || 0) <= 0 || this.priorityResetConfirmedFlag === true) {
      proceedFn()
      return
    }

    const modalElement = document.getElementById('priorityResetPauseModal')
    const confirmBtn = document.getElementById('priorityResetPauseConfirmBtn')
    if (!modalElement || !confirmBtn) {
      // Fail-safe: modal DOM unexpectedly missing while priority > 0. Proceed
      // to the underlying action so the user is not stuck behind an
      // un-dismissable dialog. The backend `priority_reset_confirmed_for_pause?`
      // guard remains authoritative — if server-side priority is still > 0 and
      // the confirmation flag is absent, the request returns 422 with a
      // generic error toast. This is acceptable degraded UX for a
      // theoretically unreachable path (the modal is server-rendered when
      // @lead_order.priority > 0).
      proceedFn()
      return
    }

    // Cancel any previously-attached (un-fired) handler so that cancel-then-retry
    // doesn't stack multiple listeners and fire proceedFn more than once.
    if (this._priorityGuardAbort) this._priorityGuardAbort.abort()
    this._priorityGuardAbort = new AbortController()

    const handler = () => {
      this.priorityResetConfirmedFlag = true
      const modal = bootstrap.Modal.getInstance(modalElement)
      if (modal) modal.hide()
      proceedFn()
    }
    confirmBtn.addEventListener('click', handler, {
      once: true,
      signal: this._priorityGuardAbort.signal
    })

    bootstrap.Modal.getOrCreateInstance(modalElement).show()
  }

  guardPauseSubscription(event) {
    event.preventDefault()
    this.guardPauseWithPriority(() => {
      const modalElement = document.getElementById('pauseSubscriptionModal')
      if (!modalElement) {
        // The button can render for contexts where the modal isn't in the DOM
        // (e.g., non-subscription orders where admin-only auth shows the button
        // but the form is guarded out). Surface this as an error because the
        // button being visible without the matching form is a rendering defect
        // that should be caught in QA, not lost among console warnings.
        console.error('[LeadOrderForm] #pauseSubscriptionModal not found — cannot open pause form')
        return
      }
      // Stamp the hidden field before the modal opens so the native form
      // includes confirmed_priority_reset=true when the user submits.
      const hidden = document.getElementById('pauseSubscriptionConfirmedPriorityReset')
      if (hidden) hidden.value = this.priorityResetConfirmedFlag ? 'true' : 'false'
      bootstrap.Modal.getOrCreateInstance(modalElement).show()
    })
  }

  // -- Aged Bucket Validation ------------------------------------------------

  validateAgedBucketChange(event) {
    if (!this.hasAgedBucketSelectTarget) return

    const select = event.currentTarget
    const currentAgedBucket = select.dataset.currentAgedBucket
    const selectedValue = select.value

    // If the order is currently aged and user selected fresh (empty value)
    if (currentAgedBucket && !selectedValue) {
      this.notifyError("This is an aged order and cannot be moved to a realtime order.")

      // Revert to the previous aged bucket value
      select.value = currentAgedBucket
    }
  }

  async toggleDisabled(event) {
    if (!this.hasDisabledToggleTarget) return

    const checkbox = this.disabledToggleTarget
    const isDisabled = checkbox.checked

    try {
      const response = await patch(
        `/lead_orders/${this.leadOrderIdValue}/toggle_disabled`,
        { body: JSON.stringify({ disabled: isDisabled }), responseKind: 'json' }
      )

      if (response.ok) {
        if (isDisabled) {
          this.notifyError('Order Disabled.')
        } else {
          this.notifySuccess('Order Enabled.')
        }
      } else {
        const data = await response.json.catch(() => ({}))
        this.notifyError(data?.error || 'Failed to update.')
        checkbox.checked = !isDisabled
      }
    } catch (error) {
      console.error('[LeadOrderForm] toggleDisabled failed:', error)
      this.notifyError('Failed to update disabled status.')
      checkbox.checked = !isDisabled
    }
  }

  // -- Google Sheet Export Cooldown ------------------------------------------

  checkGoogleSheetExportCooldown() {
    if (!this.hasGoogleSheetExportButtonTarget) return

    const cooldownUntil = this.getGoogleSheetCooldownUntil()
    if (cooldownUntil && Date.now() < cooldownUntil) {
      this.disableGoogleSheetExportButton(cooldownUntil)
    }
  }

  getGoogleSheetCooldownUntil() {
    // Use server-side timestamp if available
    if (this.lastGoogleSheetExportAtValue) {
      const exportTime = new Date(this.lastGoogleSheetExportAtValue).getTime()
      if (!Number.isNaN(exportTime)) {
        const cooldownUntil = exportTime + this.googleSheetExportTimeoutValue
        if (Date.now() < cooldownUntil) {
          return cooldownUntil
        }
      }
    }
    return null
  }

  disableGoogleSheetExportButton(cooldownUntil) {
    if (!this.hasGoogleSheetExportButtonTarget) return

    const button = this.googleSheetExportButtonTarget
    button.classList.add("disabled")
    button.setAttribute("aria-disabled", "true")
    button.style.pointerEvents = "none"

    this.updateGoogleSheetButtonText(cooldownUntil)
    this.updateGoogleSheetTimeAgo()

    // Set up interval to update remaining time and time ago text
    if (this.googleSheetCooldownInterval) {
      clearInterval(this.googleSheetCooldownInterval)
    }

    this.googleSheetCooldownInterval = setInterval(() => {
      const remaining = cooldownUntil - Date.now()
      if (remaining <= 0) {
        this.enableGoogleSheetExportButton()
      } else {
        this.updateGoogleSheetButtonText(cooldownUntil)
        this.updateGoogleSheetTimeAgo()
      }
    }, 1000)
  }

  enableGoogleSheetExportButton() {
    if (!this.hasGoogleSheetExportButtonTarget) return

    const button = this.googleSheetExportButtonTarget
    button.classList.remove("disabled")
    button.removeAttribute("aria-disabled")
    button.style.pointerEvents = ""
    button.textContent = "Google Sheet"

    if (this.googleSheetCooldownInterval) {
      clearInterval(this.googleSheetCooldownInterval)
      this.googleSheetCooldownInterval = null
    }
  }

  updateGoogleSheetButtonText(cooldownUntil) {
    if (!this.hasGoogleSheetExportButtonTarget) return

    const remaining = cooldownUntil - Date.now()
    if (remaining <= 0) return

    const minutes = Math.ceil(remaining / 60000)
    const button = this.googleSheetExportButtonTarget
    button.textContent = `Google Sheet (${minutes}m)`
  }

  updateGoogleSheetTimeAgo() {
    if (!this.hasGoogleSheetExportTimeAgoTarget || !this.lastGoogleSheetExportAtValue) return

    const exportTime = new Date(this.lastGoogleSheetExportAtValue).getTime()
    if (Number.isNaN(exportTime)) return

    const elapsed = Date.now() - exportTime
    const timeAgoText = this.formatTimeAgo(elapsed)
    this.googleSheetExportTimeAgoTarget.textContent = `${timeAgoText} ago`
  }

  formatTimeAgo(milliseconds) {
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
      return days === 1 ? "1 day" : `${days} days`
    }
    if (hours > 0) {
      return hours === 1 ? "about 1 hour" : `about ${hours} hours`
    }
    if (minutes > 0) {
      return minutes === 1 ? "1 minute" : `${minutes} minutes`
    }
    return "less than a minute"
  }

  handleGoogleSheetExport(event) {
    // Check if already in cooldown based on server timestamp
    const cooldownUntil = this.getGoogleSheetCooldownUntil()
    if (cooldownUntil && Date.now() < cooldownUntil) {
      event.preventDefault()
      event.stopPropagation()

      const minutes = Math.ceil((cooldownUntil - Date.now()) / 60000)
      if (isFunction(window.showToast)) {
        window.showToast(
          `Please wait ${minutes} more minute${minutes !== 1 ? "s" : ""} before exporting again.`,
          "bg-warning"
        )
      }
      return false
    }

    // User clicked and is not in cooldown - allow the action
    // The Rails controller will show the flash message with lead count
  }
}
